import { WhiteBox } from '../../../../../../baseComponents/AppLayout/WhiteBox'
import { BigTitleAndRightLayout } from '../../../../../../baseComponents/Layout/TitleAndRightLayout'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { faChevronLeft, faChevronRight, faSquareSliders } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import './wCalTurns.sass'
import useDictDays from '../../../../../../utils/hooks/wCalTurn/useDictDays'
import SearchBar from '../../../../../../baseComponents/SearchBar'
import { stringMatchWithSearch } from '../../../../../../utils/globals/search'
import { CustomSelectWithSearchBar } from '../../../../../../forms/CustomSelect'
import { WorkingCenterPayload } from '../../../../../../utils/reducers/company/workingCenters'
import { GroupPayload } from '../../../../../../utils/reducers/groups/groups'
import { createDictionaryForWCalTurns, getWeekDates } from './functions'
import { WCalTurnWeekTy, WCalTurnTableConfigurationTy, WCalTurnsByEmployeeTy } from './types'
import SingleEmployeeTurn from './SingleEmployeeTurn'
import { addDictionaryExtraLine } from '../../../../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../../../../forms/CustomSelect/functions'
import LoadingSpinner from '../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import PermissionChecker from '../../../../../../baseComponents/PermissionChecker'
import { ButtonPrimary, ButtonRedSecondary } from '../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import ModalDuplicateWCalTurn from './ModalDuplicateWCalTurn'
import ModalDeleteWCalTurn from './ModalDeleteWCalTurn'
import ModalConfigWCalTurn from './ModalConfigWCalTurn'
import { arrayFromDictionary } from '../../../../../../utils/globals/array'

type WorkingCalendarTurnsProps = ReduxEmployeesAndWC & ComponentWithPermissions & {}

const WorkingCalendarTurns: React.FC<WorkingCalendarTurnsProps> = ({ ...rest}) => {
    const { t } = useTwinTranslation()
    const { turnsByEmployee, getData, tableConfiguration, setTableConfiguration, openModal, setOpenModal } = useWorkingCalendarTurnsLogic()
    return (
        <BigTitleAndRightLayout title={t('turns', 'Turnos')} className='working_calendar_turns' RightHeader={<div className='flex items-center gap-15 flex-wrap'>
            <div className='text-green-21 flex items-center cursor-pointer' onClick={() => setOpenModal({type: 'edit', allRowData: {}})} key={'setUpButtton'}>
                <TwinIcon icon={faSquareSliders} className='h-19 mr-10' />
                <span><TwinTrans transKey='setUp'>Configurar</TwinTrans></span>
            </div>
            <div className='flex gap-10 flex-wrap'>
                <PermissionChecker userPermissions={rest.userPermissions} permission='delete'>
                    <ButtonRedSecondary onClick={() => setOpenModal({ type: 'delete', allRowData: {} })}>
                        {t('deleteTurns', 'Borrar turnos')}
                    </ButtonRedSecondary>
                </PermissionChecker>
                <PermissionChecker userPermissions={rest.userPermissions} permission='update'>
                    <ButtonPrimary onClick={() => setOpenModal({ type: 'create', allRowData: {} })}>
                        {t('duplicateTurns', 'Duplicar turnos')}
                    </ButtonPrimary>
                </PermissionChecker>
            </div>
        </div>}>
            <WhiteBox className='flex flex-col flex-auto py-50 px-40 h-1 overflow-hidden'>
                <WorkingCalendarTurnsTableHeader {...rest} tableConfiguration={tableConfiguration} setTableConfiguration={setTableConfiguration} />
                {turnsByEmployee === null ? <LoadingSpinner /> : <WeekCalendarTurns {...{ data: turnsByEmployee, week: tableConfiguration.week, getData, searchValue: tableConfiguration.searchValue, selectedWorkingCenter: tableConfiguration.selectedWorkingCenter }} />}
            </WhiteBox>
            {openModal?.type === 'create' ? <ModalDuplicateWCalTurn setOpened={() => setOpenModal(null)} onSubmit={getData}/>: null}
            {openModal?.type === 'delete' ? <ModalDeleteWCalTurn setOpened={() => setOpenModal(null)} onSubmit={getData} /> : null}
            {openModal?.type === 'edit' ? <ModalConfigWCalTurn setOpened={() => setOpenModal(null)} onSubmit={getData}/> : null}
        </BigTitleAndRightLayout>
    )
}

const useWorkingCalendarTurnsLogic = () => {
    const [turnsByEmployee, setTurnsByEmployee] = useState<{ [EmployeeId: number]: WCalTurnsByEmployeeTy } | null>(null)
    const [tableConfiguration, setTableConfiguration] = useState<WCalTurnTableConfigurationTy>(() => {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        const currentDay = new Date()
        currentDay.setHours(0, 0, 0, 0)
        const weekDates = getWeekDates(currentDay)
        return {
            searchValue: '',
            selectedGroup: '',
            selectedWorkingCenter: '',
            week: { day: now, weekDates }
        }
    })
    const { openModal, setOpenModal } = useEditDeleteModal()
    const myWeekForEffect = JSON.stringify(tableConfiguration.week)
    const getData = useCallback(async () => {
        const week = JSON.parse(myWeekForEffect)
        setTurnsByEmployee(null)
        const result = await twinFetchPostJSON('/api/app/workingCalendar/workingCalendarTurn/getWCalTurnsGroupByEmployee', { weekStart: week.weekDates[0], weekEnd: week.weekDates[6], GroupId: parseInt(tableConfiguration.selectedGroup || '0'), WorkingCenterId: parseInt(tableConfiguration.selectedWorkingCenter || '0') })
        if (result) {            
            setTurnsByEmployee(createDictionaryForWCalTurns(result, tableConfiguration.selectedWorkingCenter))
        }
    }, [setTurnsByEmployee, myWeekForEffect, tableConfiguration.selectedGroup, tableConfiguration.selectedWorkingCenter])

    useEffect(() => {
        getData()
    }, [getData])

    return { turnsByEmployee, getData, tableConfiguration, setTableConfiguration, openModal, setOpenModal }
}

type WorkignCalendarTurnsHeaderTableProps = ReduxEmployeesAndWC & WorkingCalendarTurnsTableHeaderLogicProps &  {
    tableConfiguration: WCalTurnTableConfigurationTy
}

const WorkingCalendarTurnsTableHeader: React.FC<WorkignCalendarTurnsHeaderTableProps> = ({ workingCenters, groups, tableConfiguration, setTableConfiguration }) => {
    const { t } = useTwinTranslation()
    const allWorkingCenters = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(workingCenters))), [workingCenters])
    const allGroups = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(groups))), [groups])
    const { changeField } = useWorkingCalendarTurnsTableHeaderLogic({ setTableConfiguration })
    return (
        <div className='flex gap-20 mb-20 items-end flex-wrap justify-between'>
            <div className='flex-wrap flex items-end gap-20'>
                <SearchBar className='search_bar_table' placeholder={t('searchDots', 'Buscar...')} searchValue={tableConfiguration.searchValue} onChange={(value) => changeField(value, 'searchValue')} />
                <CustomSelectWithSearchBar label={t('workingCenter', 'Centro de trabajo')} items={allWorkingCenters || {}} className='working_calendar_turns_select' sortFunction={emptyExtraLineFirstSortFunction} onChange={(value) => changeField(value, 'selectedWorkingCenter')} />
                <CustomSelectWithSearchBar label={t('groups', 'Grupos')} items={allGroups || {}} className='working_calendar_turns_select' sortFunction={emptyExtraLineFirstSortFunction} onChange={(value) => changeField(value, 'selectedGroup')} />
            </div>
            <WCalTurnsChangeWeek week={tableConfiguration.week} setTableConfiguration={setTableConfiguration} />
        </div>
    )
}

interface WorkingCalendarTurnsTableHeaderLogicProps {
    setTableConfiguration: React.Dispatch<React.SetStateAction<WCalTurnTableConfigurationTy>>
}

const useWorkingCalendarTurnsTableHeaderLogic = ({setTableConfiguration}: WorkingCalendarTurnsTableHeaderLogicProps) => {
    const changeField = useCallback((value: any, field: keyof WCalTurnTableConfigurationTy) => {
        setTableConfiguration((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return {...copy, [field]: value}
        })
    }, [setTableConfiguration])
    return { changeField }
}


interface WCalTurnsChangeWeekProps extends WCalTurnsChangeWeekLogicProps {
    week: WCalTurnWeekTy
}

const WCalTurnsChangeWeek: React.FC<WCalTurnsChangeWeekProps> = ({ setTableConfiguration, week }) => {
    const { nextWeek, prevWeek } = useWCalTurnsChangeWeekLogic({ setTableConfiguration })
    return (
        <div className='flex items-end calendar_toolbar flex-wrap justify-between gap-y-10 gap-x-30'>
            <div className='toolbar-label text-left text-gray-51 capitalize'>
                <TwinTrans transKey='week'>Semana</TwinTrans> {new Date(week.weekDates[0]).toLocaleDateString()} - {new Date(week.weekDates[6]).toLocaleDateString()}
            </div>
            <div className='flex'>
                <div className='calendar_toolbar_arrow'>
                    <TwinIcon className='cursor-pointer p-3 h-24  hover:text-green-21' icon={faChevronLeft} onClick={prevWeek} />
                </div>
                <div className='ml-24 calendar_toolbar_arrow'>
                    <TwinIcon className='cursor-pointer p-3 h-24 hover:text-green-21' icon={faChevronRight} onClick={nextWeek} />
                </div>
            </div>
        </div>
    )
}

interface WCalTurnsChangeWeekLogicProps {
    setTableConfiguration: React.Dispatch<React.SetStateAction<WCalTurnTableConfigurationTy>>
}

const useWCalTurnsChangeWeekLogic = ({ setTableConfiguration }: WCalTurnsChangeWeekLogicProps) => {
    const nextWeek = useCallback(() => {
        setTableConfiguration((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const week = copy.week
            const copyOldDate = new Date(week.weekDates[0])
            copyOldDate.setDate(copyOldDate.getDate() + 7)
            const weekDates = getWeekDates(copyOldDate)
            copy.week.weekDates = weekDates
            return copy
        })
    }, [setTableConfiguration])

    const prevWeek = useCallback(() => {
        setTableConfiguration((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            const week = copy.week
            const copyOldDate = new Date(week.weekDates[0])
            copyOldDate.setDate(copyOldDate.getDate() - 7)
            const weekDates = getWeekDates(copyOldDate)
            copy.week.weekDates = weekDates
            return copy
        })
    }, [setTableConfiguration])
    return { nextWeek, prevWeek }
}

interface WeekCalendarTurnsProps {
    data: { [EmployeeId: number]: WCalTurnsByEmployeeTy }
    week: WCalTurnWeekTy
    getData: () => Promise<void>
    searchValue: string
    selectedWorkingCenter: string
}

const WeekCalendarTurns: React.FC<WeekCalendarTurnsProps> = ({ data, week, getData, searchValue, selectedWorkingCenter }) => {
    const renderThis: JSX.Element[] = []
    const myDataByOrder = arrayFromDictionary(data)
    myDataByOrder.sort((a, b) => a.order - b.order)
    for (const element of myDataByOrder) {
        if (stringMatchWithSearch(searchValue, element.employee.fullname_short)) {
            renderThis.push(<SingleEmployeeTurn employeeData={element} key={element.employee.id} week={week} getData={getData} selectedWorkingCenter={selectedWorkingCenter}/>)
        }
    }
    return (
        <div className='overflow-auto week_calendar_turn_parent flex-auto h-1 mt-10 flex-col'>
            <div className='flex flex-col week_calendar_turn flex-auto'>
                <WeekCalendarTurnsHeaderRow week={week} />
                <div className='flex flex-col overflow-auto'>
                    {renderThis}
                </div>
            </div>
        </div>
    )
}

interface WeekCalendarTurnsHeaderRowProps {
    week: WCalTurnWeekTy
}

const WeekCalendarTurnsHeaderRow: React.FC<WeekCalendarTurnsHeaderRowProps> = ({ week }) => {
    const renderDays: JSX.Element[] = []
    const today = new Date(week.day)
    for (const day of week.weekDates) {
        renderDays.push(
            <WeekCalendarDays day={day} today={today} key={day} />
        )
    }
    return (
        <div className='flex border-b border-gray-EE items-center working_calendar_turn_header_row'>
            <div className='turn_employee_cell'></div>
            <div className='flex flex-auto h-full items-center'>{renderDays}</div>
        </div>
    )
}

interface WeekCalendarDaysProps {
    day: string
    today: Date
}

const WeekCalendarDays: React.FC<WeekCalendarDaysProps> = ({ day, today }) => {
    const { days } = useDictDays()
    const classNameToday = 'bg-green-43 text-white'
    const myDay = new Date(day)
    const dayKey = myDay.getDay()
    const myNumberDay = myDay.getDate()
    const isToday = today.getTime() === myDay.getTime()
    return (
        <div className='flex flex-col flex-auto items-center text-gray-51 light14 single_schedule_cell' key={dayKey}>
            <div>{(days[dayKey].name).toUpperCase()}</div>
            <div className={'flex rounded-full h-30 w-30 items-center justify-center single_schedule_cell_number ' + (isToday ? classNameToday : '')}>{myNumberDay}</div>
        </div>
    )
}

const workingCentersGroupsDispatch = {
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
}

export type ReduxEmployeesAndWC = ConnectedProps<typeof employeesConnect>
const mapEmployeesConnector = (state: AllReduxPayloads) => ({ workingCenters: state.workingCenters, groups: state.groups })
const employeesConnect = connect(mapEmployeesConnector, workingCentersGroupsDispatch)
const employeesConnectLoading = withLoading(WorkingCalendarTurns, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }])
const WorkingCalendarTurnsConnect = employeesConnect(employeesConnectLoading)

export default WorkingCalendarTurnsConnect
